import i18n from "i18n";
import ColumnHeader from "Components/UI/Table/ColumnHeader";
import {
  ITopicDnDResponse,
  ITopicStatus,
  ITopicType,
} from "Data/interfaces/Activities/IDragAndDropDataFormat";
import { ColumnProps } from "antd/lib/table";
import { IDisciplineResponse } from "Data/interfaces/Obra/IDisciplineResponse";
import { IUserData } from "Data/interfaces/User/IUserData";
import { dateToLocaleString, isExpiredDate } from "Utils/DateUtils";
import OptionCustom from "Components/UI/NewFormGroup/OptionImg";
import { Icon } from "Components/UI";
import { handlePriority, handleVisibility } from "Utils/ActivitiesUtils";
import { getTypeIcon } from "Utils/ObraUtils";
import AvatarsItems from "Pages/Activities/components/AvatarsItems";
import DisciplinesTooltip from "Pages/Activities/components/DisciplinesTooltip";
import HighlighterWords from "Components/UI/HighlightWords";
import { IMDTheme } from "Data/interfaces/MultiDomain/IMDTheme";
import Tooltip from "Components/UI/Tooltip";
import { TypeOrderType } from "../../useListVision";
import ImagePreview from "Components/UI/ImagePreview";
import ImagePreviewGroup from "Components/UI/ImagePreviewGroup";
import HeaderActionsButtons from "Pages/Activities/Modals/ShowCreateActivity/components/Attachments/components/HeaderActionsButtons";
import { IImgsOnErrorControl } from "Data/interfaces/Helper/IImgsOnErrorControl";
import { INotificationInAppTopic } from "Data/interfaces/Notification/INotification";
import {
  actionImgPrint,
  actionDownloadImage,
  actionImgOpenInNewWindow,
} from "Utils/TextUtils";
import {
  ColumnDate,
  ColumnDisciplines,
  ColumnIdentifier,
  ColumnPriority,
  ColumnStatus,
  ColumnTitle,
  ColumnType,
  ColumnUsers,
} from "./styles";

import imgPlaceholder from 'Static/images/image_placeholder.webp';

export interface IResizableColumnProps {
  active: boolean;
  columns: Record<string, number>;
};

export const RESIZABLE_ACTIVITIES_COLUMN_IDS: Record<string, string> = {
  Identifier: 'identifier',
  Visibility: 'visibility',
  Title: 'title',
  Type: 'type',
  Priority: 'priority',
  ConstructionSiteDisciplines: 'construction-site-disciplines',
  TopicAssignedUsers: 'topic-assigned-users',
  DueDate: 'due-date',
  Status: 'status',
};

export const minWidthResizableActivitiesColumns: Record<string, number> = {
  [RESIZABLE_ACTIVITIES_COLUMN_IDS.Identifier]: 76,
  [RESIZABLE_ACTIVITIES_COLUMN_IDS.Visibility]: 40,
  [RESIZABLE_ACTIVITIES_COLUMN_IDS.Title]: 220,
  [RESIZABLE_ACTIVITIES_COLUMN_IDS.Type]: 130,
  [RESIZABLE_ACTIVITIES_COLUMN_IDS.Priority]: 96,
  [RESIZABLE_ACTIVITIES_COLUMN_IDS.ConstructionSiteDisciplines]: 168,
  [RESIZABLE_ACTIVITIES_COLUMN_IDS.TopicAssignedUsers]: 140,
  [RESIZABLE_ACTIVITIES_COLUMN_IDS.DueDate]: 86,
  [RESIZABLE_ACTIVITIES_COLUMN_IDS.Status]: 120,
};

interface IGetColumnsTableActivities {
  columnsWidth: IResizableColumnProps['columns'];
  searchActivity: string;
  theme: IMDTheme;
  currentAttachmentIndex: number;
  imgsOnErrorControl: IImgsOnErrorControl;
  showPreview: Map<string, boolean>;
  inAppTopicNotifications: INotificationInAppTopic[];
  onOpenShowActivity: (activity: string) => void;
  onOrderOpt: (orderType?: TypeOrderType, sorterOrder?: boolean) => void;
  setCurrentAttachmentIndex: (index: number) => void;
  onImgOnError: (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
    id: string,
    originalURL: string
  ) => void;
  onPreview: (id: string, visible: boolean) => void;
}

export const getColumnsTableActivities: (input: IGetColumnsTableActivities) => Array<ColumnProps<any>> = ({
  columnsWidth,
  searchActivity,
  theme,
  currentAttachmentIndex,
  showPreview,
  onOpenShowActivity,
  onOrderOpt,
  setCurrentAttachmentIndex,
  onImgOnError,
  onPreview,
}) => {
  return [
    {
      dataIndex: 'Identifier',
      width: columnsWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Identifier],
      title: (
        <ColumnHeader
          title={i18n.t('activities:listColumns.code')}
          sorter={(sortDirection) => onOrderOpt('Identifier', sortDirection)}
        />
      ),
      render: (text: string, record: ITopicDnDResponse) => (
        <ColumnIdentifier>{`#${text}`}</ColumnIdentifier>
      ),
    },
    {
      dataIndex: 'Visibility',
      width: columnsWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Visibility],
      title: (
        <ColumnHeader
          title={i18n.t('activities:listColumns.version')}
          showOrder={false}
        />
      ),
      render: (text: number, record: ITopicDnDResponse) => (
        <span>
          <Icon
            icon={handleVisibility(text, theme).icon}
            customSize={handleVisibility(text, theme).size}
            customColor={handleVisibility(text, theme).color}
          />
        </span>
      ),
    },
    {
      dataIndex: 'Title',
      width: columnsWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Title],
      title: (
        <ColumnHeader
          title={i18n.t('activities:listColumns.summary')}
          sorter={(sortDirection) => onOrderOpt('Title', sortDirection)}
        />
      ),
      render: (text: string, record: ITopicDnDResponse) => {
        const currentAttachmentPreview = record.Attachments[currentAttachmentIndex];
        const hasComments = record.CommentCount > 0;
        const commentsLength = record.CommentCount > 99
          ? '99+'
          : record.CommentCount;

        return (
          <ColumnTitle>
            <span className="titleResume" onClick={() => onOpenShowActivity(record.Guid)}>
              <HighlighterWords
                text={text}
                wordSearched={searchActivity}
                className="highlighterWords"
              />
            </span>
            <span className="titleContent">
              {hasComments && (
                <span className="cardCommentContainer">
                  <Icon
                    icon="doubleChat"
                    customSize={16}
                    className="cardCommentIcon"
                  />
                  <span className="cardCommentText">{commentsLength}</span>
                </span>
              )}
              {record?.Attachments && record.Attachments.length > 0 && (
                <>
                  {record.Attachments.length === 1 && (
                    <div className="singleImgContainer">
                      <ImagePreview
                        isUnique
                        srcPreview={record.Attachments[0].FileURL}
                        className="img"
                        src={record.Attachments[0].Thumbnail64x64Url}
                        srcName={record.Attachments[0].File.Name}
                        alt=""
                        onError={(e) => e.currentTarget.src = imgPlaceholder}
                        HeaderButtons={(
                          <HeaderActionsButtons
                            onPrint={() => {
                              actionImgPrint(record.Attachments[0].FileURL);
                            }}
                            onDownload={() => {
                              actionDownloadImage(record.Attachments[0].FileURL, record.Attachments[0].File.Name);
                            }}
                            onOpen={() => {
                              actionImgOpenInNewWindow(record.Attachments[0].FileURL);
                            }}
                          />
                        )}
                      />
                    </div>
                  )}
                  {record.Attachments.length > 1 && (
                    <ImagePreviewGroup
                      imagesForShow={record.Attachments.map(att => att.FileURL)}
                      srcName={currentAttachmentPreview?.File.Name}
                      visible={showPreview.get(record.Guid)}
                      visibleOn
                      onChangeVisible={(visible) => onPreview(record.Guid, visible)}
                      current={currentAttachmentIndex}
                      HeaderButtons={(
                        <HeaderActionsButtons
                          onPrint={() => {
                            if (currentAttachmentPreview) {
                              actionImgPrint(currentAttachmentPreview.FileURL);
                            }
                          }}
                          onDownload={() => {
                            if (currentAttachmentPreview) {
                              actionDownloadImage(currentAttachmentPreview.FileURL, currentAttachmentPreview.File.Name);
                            }
                          }}
                          onOpen={() => {
                            if (currentAttachmentPreview) {
                              actionImgOpenInNewWindow(currentAttachmentPreview.FileURL);
                            }
                          }}
                        />
                      )}
                      onCurrentIndex={setCurrentAttachmentIndex}
                    >
                      <div className="multipleImgContainer" onClick={() => onPreview(record.Guid, true)}>
                        <img
                          className="img"
                          src={record.Attachments[0].Thumbnail64x64Url}
                          alt=""
                          onError={(e) => e.currentTarget.src = imgPlaceholder}
                        />
                        <img
                          className="img"
                          src={record.Attachments[1].Thumbnail64x64Url}
                          alt=""
                          onError={(e) => e.currentTarget.src = imgPlaceholder}
                        />
                        <span className="imgCount">
                          <span className="textCount">
                            {`+${record.Attachments.length - 1}`}
                          </span>
                        </span>
                      </div>
                    </ImagePreviewGroup>
                  )}
                </>
              )}
              {record.ConstructionSiteDisciplines.length <= 0 && (
                <Tooltip
                  placement="top"
                  overlay="Erro no cadastro da atividade"
                >
                  <span>
                    <Icon
                      icon="aviso"
                      customSize={14}
                      className="cardIconAlarm"
                    />
                  </span>
                </Tooltip>
              )}
            </span>
          </ColumnTitle>
        )
      },
    },
    {
      dataIndex: 'Type',
      width: columnsWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Type],
      title: (
        <ColumnHeader
          title={i18n.t('activities:listColumns.type')}
          sorter={(sortDirection) => onOrderOpt('Type', sortDirection)}
        />
      ),
      render: (text: ITopicType, record: ITopicDnDResponse) => (
        <ColumnType>
          <Icon
            icon={getTypeIcon(text.Name || '')}
            customSize={14}
            className="cardIconHeader"
          />
          <span className="text">{text.Name}</span>
        </ColumnType>
      ),
    },
    {
      dataIndex: 'Priority',
      width: columnsWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Priority],
      title: (
        <ColumnHeader
          title={i18n.t('activities:listColumns.priority')}
          sorter={(sortDirection) => onOrderOpt('Priority', sortDirection)}
        />
      ),
      render: (text: number, record: ITopicDnDResponse) => (
        <ColumnPriority customcolor={handlePriority(text, theme).color}>
          <Icon
            icon={handlePriority(text, theme).icon}
            customSize={handlePriority(text, theme).size}
            customColor={handlePriority(text, theme).color}
          />
          <span className="text">{handlePriority(text, theme).label}</span>
        </ColumnPriority>
      ),
    },
    {
      dataIndex: 'ConstructionSiteDisciplines',
      width: columnsWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.ConstructionSiteDisciplines],
      title: (
        <ColumnHeader
          title={i18n.t('activities:listColumns.disciplines')}
          sorter={(sortDirection) => onOrderOpt('ConstructionSiteDisciplines', sortDirection)}
        />
      ),
      render: (text: IDisciplineResponse[], record: ITopicDnDResponse) => (
        <ColumnDisciplines>
          <DisciplinesTooltip title={i18n.t('activities:listColumns.disciplines')} disciplines={text} />
        </ColumnDisciplines>
      ),
    },
    {
      dataIndex: 'TopicAssignedUsers',
      width: columnsWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.TopicAssignedUsers],
      title: (
        <ColumnHeader
          title={i18n.t('activities:listColumns.responsibles')}
          sorter={(sortDirection) => onOrderOpt('TopicAssignedUsers', sortDirection)}
        />
      ),
      render: (text: IUserData[], record: ITopicDnDResponse) => (
        <ColumnUsers>
          <AvatarsItems users={text} showDetail />
        </ColumnUsers>
      ),
    },
    {
      dataIndex: 'DueDate',
      width: columnsWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.DueDate],
      title: (
        <ColumnHeader
          title={i18n.t('activities:listColumns.deadline')}
          sorter={(sortDirection) => onOrderOpt('DueDate', sortDirection)}
        />
      ),
      render: (text: Date | null, record: ITopicDnDResponse) => (
        text ? (
          <ColumnDate error={`${isExpiredDate(text)}`}>
            {dateToLocaleString(text)}
          </ColumnDate>
        ) : (
          <></>
        )
      ),
    },
    {
      dataIndex: 'Status',
      width: columnsWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Status],
      title: (
        <ColumnHeader
          title={i18n.t('activities:listColumns.status')}
          sorter={(sortDirection) => onOrderOpt('Status', sortDirection)}
        />
      ),
      render: (text: ITopicStatus, record: ITopicDnDResponse) => (
        <ColumnStatus>
          <OptionCustom
            label={text.Name}
            bgColor={`${text.Color}26`}
            textColor={`${text.Color}`}
          />
        </ColumnStatus>
      ),
    }
  ];
};
